/**
 * AddToListSelectModal - Modal for selecting which list to add cards to
 *
 * Used when adding individual cards or bulk cards (like deck needs) to a list.
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';
import { FaPlus } from 'react-icons/fa';
import { IoCheckmark, IoList } from 'react-icons/io5';

import Modal from '@/components/Modal/Modal';
import Spinner from '@/components/Spinner/Spinner';

import { useLists } from '../hooks/api';

interface AddToListSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (listId: string) => void;
  onCreateList?: () => void;
  title: string;
  description?: string;
  isLoading?: boolean;
  selectedListId?: string;
}

export default function AddToListSelectModal({
  isOpen,
  onClose,
  onSelect,
  onCreateList,
  title,
  description,
  isLoading = false,
  selectedListId,
}: AddToListSelectModalProps): ReactElement {
  const { data: lists, isLoading: listsLoading } = useLists();

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="sm">
      <div className="space-y-4">
        {/* Description */}
        {description && <p className="text-silver text-sm">{description}</p>}

        {/* Lists */}
        <div className="border-storm max-h-64 overflow-y-auto rounded-lg border">
          {listsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner size="sm" />
            </div>
          ) : lists.length === 0 ? (
            <p className="text-ash py-6 text-center text-sm">No lists yet</p>
          ) : (
            <div className="divide-storm divide-y">
              {lists.map((list) => (
                <button
                  key={list.id}
                  onClick={() => onSelect(list.id)}
                  disabled={isLoading}
                  className={clsx(
                    'flex w-full items-center gap-3 px-4 py-3 text-left transition-colors',
                    selectedListId === list.id ? 'bg-aether/20 text-aether' : 'text-silver hover:bg-storm/50 hover:text-pearl',
                    isLoading && 'pointer-events-none opacity-60',
                  )}
                >
                  <span
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-sm"
                    style={list.color ? { backgroundColor: list.color } : { backgroundColor: 'var(--color-slate)' }}
                  >
                    {list.icon || <IoList className="h-4 w-4" />}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{list.name}</span>
                  {selectedListId === list.id && <IoCheckmark className="h-5 w-5 shrink-0" />}
                  {isLoading && selectedListId === list.id && <Spinner size="sm" />}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Create new list button */}
        {onCreateList && (
          <button
            onClick={onCreateList}
            disabled={isLoading}
            className={clsx(
              'text-aether hover:bg-aether/10 flex w-full items-center gap-2 rounded-lg px-4 py-3 text-sm font-medium transition-colors',
              isLoading && 'pointer-events-none opacity-60',
            )}
          >
            <FaPlus className="h-3.5 w-3.5" />
            Create new list
          </button>
        )}

        {/* Cancel button */}
        <div className="border-storm flex justify-end border-t pt-4">
          <button onClick={onClose} disabled={isLoading} className="text-ash hover:text-pearl px-4 py-2 text-sm transition-colors">
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
}
